<template>
	<v-app>
		<navbar v-if="!loading"/>
		<v-main>
			<v-layout fill-height>
				<v-flex xs12>
					<div v-if="loading" class="fill-height d-flex justify-center align-center">
						<v-progress-circular
							color="primary"
							size="56"
							indeterminate
						/>
					</div>
					<v-container
						v-else
						fluid
						class="fill-height align-start"
						:class="{
							'pa-0': ['questions-bank', 'question', 'exams', 'exam'].includes($route.name)
						}"
					>
						<router-view class="flex-grow-1" style="width: 100%"/>
					</v-container>
				</v-flex>
			</v-layout>
		</v-main>

		<v-snackbar
            v-model="snackbar"
			id="app-snackbar"
			app
            :color="
				isAlert
				? 'warning'
				: (isError ? 'red lighten-1' : 'primary')
			"
			class="text-pre-line"
        >
            {{ message }}
            <template v-slot:action="{ attrs }">
				<v-btn
					color="white"
					icon
					v-bind="attrs"
					@click="snackbar = false"
				>
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>
	</v-app>
</template>

<script>
import Navbar from '@/components/Navbar.vue';

export default {
	name: 'App',
	components: {
		Navbar,
	},

	data: () => ({
		loading: false,

		snackbar: false,
		isError: false,
		isAlert: false,
		message: '',
	}),

	created() {
		this.loading = true;
		Promise.all([
			this.$store.dispatch('classes/fetchAll'),
			this.$store.dispatch('teachers/fetchAll'),
		]).finally(() => {
			this.loading = false;
		});

		this.$eventBus.$on('show-snackbar', (state, message) => {
			this.snackbar = true;
			if (['error', 'alert'].includes(state)) {
				this.isError = state === 'error';
				this.isAlert = state === 'alert';
				this.message = message;
			} else {
				this.isError = false;
				this.isAlert = false;
				this.message = state;
			}
		});
	},

	metaInfo() {
		return {
			titleTemplate: '%s | إديونكس',
			title: this.$route.meta.title || '',
		}
	},
};
</script>

<style lang="scss">
@import "@beetronix/bee-components/lib/css/index.css";
@import "@/scss/main.scss";
@import "@/assets/font-icons/icomoon/style.css";

#app {
	.v-slide-group__prev--disabled,
	.v-slide-group__next--disabled {
		display: none;
	}
	.v-data-table,
	.v-btn:not(.v-btn--fab, .v-btn--icon, .v-btn--rounded, .v-btn-toggle > .v-btn.v-btn),
	.v-menu__content {
		border-radius: $border-radius-root;
	}
	.v-treeview-node__root {
		.v-icon {
			font-size: 18px;
			padding-top: 3px;
			padding-bottom: 3px;
		}
		.v-treeview-node__level:first-child {
			width: 0;
		}
	}

	input {
		&[type="date"], &[type="time"], &[type="datetime-local"] {
			&::-webkit-calendar-picker-indicator {
				display: none;
				-webkit-appearance: none; /* For Chrome, Safari, and newer Edge versions */
			}
		}
	}

	.no-input-border {
		.v-input__slot::before, .v-input__slot::after {
			display: none !important;
		}
	}

	.rotate-header-90 {
		transform: rotate(180deg);
		// white-space: nowrap;
		writing-mode: vertical-rl;
	}

	.question-title p { margin-bottom: 0; }

	.sub-content-icon {
		border: 1px solid #fc5e33;
		background: #fc5e33;
	}

	.drag-item {
		outline: 1px dashed #3a7bca;
		border-radius: $border-radius-root;
	}

	.error-border {
		border: 1px solid #fc3a33 !important;
		border-radius: $border-radius-root;
	}

	.flat-checkbox.v-input--checkbox {
		.v-input__slot {
			justify-content: center;
		}
		.v-input--selection-controls__input {
			margin: 0;
		}
	}

	input{
		&:focus, &:focus-within {
			outline: none;
		}
		&.full-input {
			overflow-y: hidden;
			width: 100%;
			height: 40px;
		}
		&.hide-spin-buttons {
			&::-webkit-outer-spin-button,
			&::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0;}
			&[type="number"] { -moz-appearance: textfield; }
		}
	}

	.color {
		&-1, &-2, &-3, &-4, &-5, &-6 { color: white; }
		&-1 { background-color: #3570B6; }
		&-2 { background-color: #866DF2; }
		&-3 { background-color: #18A503; }
		&-4 { background-color: #FC5E33; }
		&-5 { background-color: #FA0A78; }
		&-6 { background-color: #7A4269; }

		&-1-light, &-2-light,&-3-light,&-4-light,&-5-light,&-6-light { color: black; }
		&-1-light { background-color: #c2d6ee; }
		&-2-light { background-color: #d9d2fb; }
		&-3-light { background-color: #b7e3b1; }
		&-4-light { background-color: #fbccbf; }
		&-5-light { background-color: #fdb3d5; }
		&-6-light { background-color: #d6c4d1; }

		&-1-opacity, &-2-opacity,&-3-opacity,&-4-opacity,&-5-opacity,&-6-opacity { color: black; }
		&-1-opacity { background-color: #3570B622; }
		&-2-opacity { background-color: #866DF222; }
		&-3-opacity { background-color: #18A50322; }
		&-4-opacity { background-color: #FC5E3322; }
		&-5-opacity { background-color: #FA0A7822; }
		&-6-opacity { background-color: #7A426922; }

		&-1-text { color: #3570B6; }
		&-2-text { color: #866DF2; }
		&-3-text { color: #18A503; }
		&-4-text { color: #FC5E33; }
		&-5-text { color: #FA0A78; }
		&-6-text { color: #7A4269; }
	}

	.content-margin-0 {
		* { margin: 0; }
	}
}

*::-webkit-scrollbar{
    height: 5px;
    width: 5px;
	background-color: #f8f8f8;
}
*::-webkit-scrollbar-thumb{
    background-color: #888;
    border-radius: 10px;
}
</style>
